import type React from "react";
import { type ReactNode, useEffect, useState } from "react";
import "./utils/GlobalStyle.css";
import "./theme/reducedMotion.css";
import ErrorBoundary from "./components/ErrorBoundary";
import { DeviceContext } from "./contexts/DeviceContext";
import { defaultTheme } from "./theme/theme.css";
import type { DeviceType } from "./utils/consts";

interface UiKitThemeProps {
  children: ReactNode;
  themeClass?: string; // Optional theme override class
  dataDevice?: "desktop" | "tablet" | "totem"; // Device type for responsive selectors
  fallback?: ReactNode; // Custom error boundary fallback UI
}

const UiKitTheme: React.FC<UiKitThemeProps> = ({
  children,
  themeClass,
  dataDevice = "desktop",
  fallback,
}) => {
  const resolvedTheme = themeClass || defaultTheme;
  const [contextDevice, setContextDevice] = useState<DeviceType>(dataDevice);

  // Apply the theme class and data-device to <html> so CSS variables are
  // available inside portals (modals, dropdowns, etc.) that render outside
  // the wrapper div.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add(resolvedTheme);
    root.dataset.device = dataDevice;

    return () => {
      root.classList.remove(resolvedTheme);
      delete root.dataset.device;
    };
  }, [resolvedTheme, dataDevice]);

  // Single MutationObserver for device detection — shared across all components
  useEffect(() => {
    setContextDevice(dataDevice);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-device"
        ) {
          const element = mutation.target as HTMLElement;
          setContextDevice(
            (element.getAttribute("data-device") || "desktop") as DeviceType,
          );
        }
      }
    });

    const root = document.documentElement;
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["data-device"],
    });

    return () => observer.disconnect();
  }, [dataDevice]);

  return (
    <DeviceContext.Provider value={contextDevice}>
      <ErrorBoundary fallback={fallback}>{children}</ErrorBoundary>
    </DeviceContext.Provider>
  );
};

export default UiKitTheme;
